/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/fields/ClienteDetailsDisplay.tsx
 * Release: v1.0.0
 * Descrizione: Componente per visualizzare dettagli cliente selezionato
 */

import React from 'react';

interface Cliente {
  id: string;
  email: string;
  name?: string;
  surname?: string;
  customer_type?: 'company' | 'individual';
  company_name?: string;
  vat_number?: string;
  tax_code?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  phone_country_code?: string;
  created_at?: string;
}

interface ClienteDetailsDisplayProps {
  value?: Cliente;
  hideLabel?: boolean;
  className?: string;
}

export const ClienteDetailsDisplay: React.FC<ClienteDetailsDisplayProps> = ({
  value: cliente,
  hideLabel = false,
  className = ''
}) => {
  if (!cliente) {
    return null;
  }

  const isCompany = cliente.customer_type === 'company';
  const isIndividual = cliente.customer_type === 'individual';

  return (
    <div className={`bg-slate-700/30 border border-slate-600 rounded-lg p-4 ${className}`}>
      {!hideLabel && (
        <div className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
          <span className="text-lg">👤</span>
          Dettagli Cliente
        </div>
      )}

      <div className="space-y-3">
        {/* Dati azienda */}
        {isCompany && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-blue-400">
                <span className="text-lg">🏢</span>
                <span className="font-medium">Dati Azienda</span>
              </div>
              
              {cliente.company_name && (
                <div>
                  <span className="text-xs text-slate-400">Ragione Sociale</span>
                  <div className="text-white font-medium">{cliente.company_name}</div>
                </div>
              )}
              
              {cliente.vat_number && (
                <div>
                  <span className="text-xs text-slate-400">Partita IVA</span>
                  <div className="text-white font-mono">{cliente.vat_number}</div>
                </div>
              )}
              
              {cliente.tax_code && (
                <div>
                  <span className="text-xs text-slate-400">Codice Fiscale</span>
                  <div className="text-white font-mono">{cliente.tax_code}</div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-400">
                <span className="text-lg">📧</span>
                <span className="font-medium">Contatti</span>
              </div>
              
              <div>
                <span className="text-xs text-slate-400">Email</span>
                <div className="text-white">{cliente.email}</div>
              </div>
              
              {cliente.phone_number && (
                <div>
                  <span className="text-xs text-slate-400">Telefono</span>
                  <div className="text-white">
                    {cliente.phone_country_code && `+${cliente.phone_country_code} `}
                    {cliente.phone_number}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dati privato */}
        {isIndividual && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-blue-400">
                <span className="text-lg">👤</span>
                <span className="font-medium">Dati Personali</span>
              </div>
              
              {(cliente.first_name || cliente.last_name || cliente.name || cliente.surname) && (
                <div>
                  <span className="text-xs text-slate-400">Nome e Cognome</span>
                  <div className="text-white font-medium">
                    {cliente.first_name || cliente.name || ''} {cliente.last_name || cliente.surname || ''}
                  </div>
                </div>
              )}
              
              {cliente.tax_code && (
                <div>
                  <span className="text-xs text-slate-400">Codice Fiscale</span>
                  <div className="text-white font-mono">{cliente.tax_code}</div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-400">
                <span className="text-lg">📧</span>
                <span className="font-medium">Contatti</span>
              </div>
              
              <div>
                <span className="text-xs text-slate-400">Email</span>
                <div className="text-white">{cliente.email}</div>
              </div>
              
              {cliente.phone_number && (
                <div>
                  <span className="text-xs text-slate-400">Telefono</span>
                  <div className="text-white">
                    {cliente.phone_country_code && `+${cliente.phone_country_code} `}
                    {cliente.phone_number}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dati generici se tipo non specificato */}
        {!isCompany && !isIndividual && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-blue-400">
              <span className="text-lg">👤</span>
              <span className="font-medium">Informazioni Cliente</span>
            </div>
            
            <div>
              <span className="text-xs text-slate-400">Email</span>
              <div className="text-white">{cliente.email}</div>
            </div>
            
            {(cliente.name || cliente.surname) && (
              <div>
                <span className="text-xs text-slate-400">Nome</span>
                <div className="text-white">
                  {cliente.name || ''} {cliente.surname || ''}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer con info aggiuntive */}
        <div className="border-t border-slate-600 pt-3 mt-3">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>ID Cliente: {cliente.id}</span>
            {cliente.created_at && (
              <span>
                Registrato: {new Date(cliente.created_at).toLocaleDateString('it-IT')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};